import React from 'react';
import cn from 'classnames';
import useReduxSelector from '@/Framework/hooks/useReduxSelector';
import tableStyles from '@/finsight/ui/components/strats/StratsTable/stratsTable.scss';
import { useStratsContext } from '@/finsight/application/Strats/StratsContext';
import { CheckboxField } from '@/Framework/UI/Organisms/Filters/Checkboxer';
import { BodyCellPure } from '@dealroadshow/uikit/core/components/Table/DataTable';
import Checkbox from '@dealroadshow/uikit/core/components/Checkbox';
import * as constants from '@/finsight/ui/components/strats/constants';
import { formatColumns, getRangeIdFromCheckboxName } from '@/finsight/ui/components/strats/helpers';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import styles from './stratsRow.scss';
import alignStyles from '@dealroadshow/uikit/core/styles/helpers/align.scss';

interface IProps {
  isTotal?: boolean,
  isDivider?: boolean,
  row: any,
  ranges: any[],
}

const StratsRow = (props: IProps) => {
  const checkbox = React.createRef();
  const onlyButton = React.createRef();
  // @ts-ignore
  const checkboxes = useReduxSelector((state) => state.checkboxes);
  const {
    updatePopoverPosition,
    filterStratsByRangeIdOnly,
    filterStratsByRangeId,
    metadata: { columns },
  } = useStratsContext();

  const onOnlyClick = () => {
    // @ts-ignore
    updatePopoverPosition(onlyButton.current, `only-button-${ props.row.checkboxFieldName }`);
    filterStratsByRangeIdOnly(props.row.checkboxFieldName, props.row.checkboxParentFieldName, props.ranges, checkboxes);
  };

  const onCheckboxClick = (field) => {
    // @ts-ignore
    updatePopoverPosition(checkbox.current, props.row.checkboxFieldName);
    filterStratsByRangeId(getRangeIdFromCheckboxName(field));
  };

  const rangeCellCallback = (cellProps) => {
    if (props.isTotal) {
      return (
        <span className={ styles.totalLabel }>{ cellProps.row[cellProps.row.checkboxParentFieldName] }</span>
      );
    }
    return (
      <div className={ styles.rangeCell }>
        <div
          id={ cellProps.row.checkboxFieldName }
          // @ts-ignore
          ref={ checkbox }
          className={ styles.subCellContainer }
        >
          <CheckboxField
            defaultChecked
            name={ cellProps.row.checkboxFieldName }
            dataTest={ cellProps.row.checkboxFieldName }
            parent={ cellProps.row.checkboxParentFieldName }
            component={ Checkbox }
            label={ cellProps.row[cellProps.row.checkboxParentFieldName] }
            controlClassName={ tableStyles.checkboxControl }
            onChange={ onCheckboxClick }
            labelClassName={ tableStyles.checkboxLabel }
          />
        </div>
        <div className={ styles.subCellContainer }>
          <Button
            variant={ ButtonVariantType.text }
            className={ cn(styles.onlyButton, styles.showOnRowHoverHelper) }
            onClick={ onOnlyClick }
            title="Only"
            dataTest="onlyButton"
          />
          <span
            id={ `only-button-${ cellProps.row.checkboxFieldName }` }
            // @ts-ignore
            ref={ onlyButton }
          />
        </div>
      </div>
    );
  };

  const valueCellCallback = (cellProps) => {
    const name = cellProps.cellName;
    const value = cellProps.row[name];
    return (value === 0 && ((cellProps.row.loansCount === 0) || (cellProps.row.leasesCount === 0)))
      ? constants.STRATS_TABLE_NA_LABEL
      : formatColumns(name, value);
  };

  const renderValueCell = (columnKey) => (
    <BodyCellPure
      key={ `${ columnKey }-${ props.row.rangeId }` }
      name={ columnKey }
      className={ cn(styles.valueCell, alignStyles.alignRight) }
      rowIndex={ props.row.rangeId }
      rowData={ props.row }
      cellCallback={ valueCellCallback }
    />
  );

  return (
    <tr
      className={ cn(styles.stratsRow, { [styles.isDivider]: props.isDivider }) }
      data-test={ `row-${ props.isTotal ? 'total' : props.row.rangeId }` }
    >
      <BodyCellPure
        name={ props.row.checkboxParentFieldName }
        rowIndex={ props.row.rangeId }
        rowData={ props.row }
        cellCallback={ rangeCellCallback }
      />
      { Object.keys(columns).map(renderValueCell) }
    </tr>
  );
};

export default StratsRow;
